import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  initializeRecaptchaConfig,
  type Auth,
} from 'firebase/auth';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';
import { isPrivateSharePath } from '@/lib/private-share-path';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'demo.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'demo-project',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'demo.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:123456789:web:abcdef',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Only initialize Firebase if API key is properly configured
let app: ReturnType<typeof initializeApp> | undefined;
let auth: Auth | undefined;
let googleProvider: GoogleAuthProvider | undefined;
let appleProvider: OAuthProvider | undefined;
let analytics: Analytics | undefined;

try {
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  auth = getAuth(app);
  googleProvider = new GoogleAuthProvider();

  // Force account selection every time
  googleProvider.setCustomParameters({
    prompt: 'select_account'
  });

  appleProvider = new OAuthProvider('apple.com');
  appleProvider.addScope('email');
  appleProvider.addScope('name');

  // Isolated from the outer catch so a measurementId issue never disables auth.
  // Never on a share-link URL: the token in the path is a credential, and the
  // Analytics SDK reports the full page URL on its own (automatic page_view).
  // `analytics` stays undefined there; services/analytics.ts no-ops without it.
  if (firebaseConfig.measurementId && !isPrivateSharePath(window.location.pathname)) {
    try {
      analytics = getAnalytics(app);
    } catch (analyticsError) {
      console.warn('Firebase Analytics initialization failed:', analyticsError);
    }
  }

  // Try IndexedDB-backed persistence; fall back to sessionStorage if IndexedDB
  // is unavailable (e.g. Safari with broken IDB after a Mac migration).
  setPersistence(auth, browserLocalPersistence).catch(() => {
    setPersistence(auth, browserSessionPersistence).catch(() => {
      // In-memory only as last resort — auth state won't survive a refresh.
      console.warn('Firebase: all persistence mechanisms failed; auth will be in-memory only.');
    });
  });

  // Required for Firebase v10.6+ phone auth: fetches reCAPTCHA Enterprise config
  // so the SDK knows which verification flow to use. Safe to call and ignore errors.
  initializeRecaptchaConfig(auth).catch(() => {});
} catch (error) {
  console.warn('Firebase initialization failed - Firebase features will be disabled:', error);
}

export { app, auth, googleProvider, appleProvider, analytics };

